import type { Perm } from "../auth/AuthContext";
import type { MsgKey } from "../i18n";

export interface NavItem {
  to: string;
  key: MsgKey;
  perm?: Perm;
}

/** A run of links inside a top-bar dropdown, optionally under a heading. */
export interface NavGroup {
  headingKey?: MsgKey;
  items: NavItem[];
}

/**
 * Separates someone who edits the standards from someone who only reads them.
 * ADMIN_PERMS grants POLICY_MANAGE and RULE_MANAGE together, so either one is a
 * sufficient discriminator for the whole standards menu.
 */
export const STANDARDS_EDIT_PERM: Perm = "RULE_MANAGE";

// Gated on the *_VIEW permissions, which every role holds: a requester needs to
// know which expressions are forbidden and which policies govern their material
// before submitting it. Editing stays behind *_MANAGE, checked per control.
export const STANDARDS_NAV: NavItem[] = [
  { to: "/policies", key: "navPolicies", perm: "POLICY_VIEW" },
  { to: "/rules", key: "navRules", perm: "RULE_VIEW" },
  { to: "/educate-sage", key: "navEducateSage", perm: "RULE_VIEW" },
  { to: "/dictionary", key: "navDictionary", perm: "RULE_VIEW" },
  { to: "/sage-workflow", key: "navSageWorkflow", perm: "RULE_VIEW" },
];

/** The dropdown host for the read-only standards pages in the top bar. */
export const ASK_SAGE_PATH = "/ask-sage";

// The daily work, so these sit directly in the top bar; standards and
// operations are dropdowns on either side of them.
export const REVIEW_NAV: NavItem[] = [
  { to: "/", key: "navDashboard", perm: "REVIEW_CREATE" },
  { to: "/reviews", key: "navReviews", perm: "REVIEW_CREATE" },
  { to: ASK_SAGE_PATH, key: "navAskSage", perm: "ASK_SAGE_USE" },
  { to: "/support", key: "navSupport", perm: "SUPPORT_CREATE" },
];

export const OPERATIONS_NAV: NavItem[] = [
  { to: "/config", key: "navConfig", perm: "CONFIG_MANAGE" },
  // Next to the settings it justifies: the evidence is what an administrator
  // reads before deciding whether to widen automatic clearance.
  { to: "/ai-quality", key: "navAiQuality", perm: "AI_QUALITY_VIEW" },
  { to: "/members", key: "navMembers", perm: "MEMBER_VIEW" },
  { to: "/queue-health", key: "navQueueHealth", perm: "AUDIT_VIEW" },
  { to: "/audit", key: "navAudit", perm: "AUDIT_VIEW" },
  { to: "/ask-sage-log", key: "navAskSageLog", perm: "ASK_SAGE_VIEW_ORG" },
];

const SUPER_ADMIN_HOME = "/super-admin";

/** Platform-admin pages live under this prefix so the area is one subtree. */
export const SUPER_ADMIN = {
  home: SUPER_ADMIN_HOME,
  organizations: `${SUPER_ADMIN_HOME}/organizations`,
  vaultsageOrphans: `${SUPER_ADMIN_HOME}/vaultsage-orphans`,
  adminUsers: `${SUPER_ADMIN_HOME}/admin-users`,
  adminUser: (userId: string) => `${SUPER_ADMIN_HOME}/admin-users/${encodeURIComponent(userId)}`,
  supportInbox: `${SUPER_ADMIN_HOME}/support-inbox`,
  askSageLog: `${SUPER_ADMIN_HOME}/ask-sage-log`,
  engineQuality: `${SUPER_ADMIN_HOME}/engine-quality`,
  llmSettings: `${SUPER_ADMIN_HOME}/llm-settings`,
  supportInboxTicket: (id: string) =>
    `${SUPER_ADMIN_HOME}/support-inbox/${encodeURIComponent(id)}`,
} as const;

export const SUPER_ADMIN_NAV: NavItem[] = [
  { to: SUPER_ADMIN.home, key: "navSuperAdmin" },
  { to: SUPER_ADMIN.organizations, key: "navOrganizations" },
  { to: SUPER_ADMIN.vaultsageOrphans, key: "navVaultsageOrphans" },
  { to: SUPER_ADMIN.adminUsers, key: "navAdminUsers" },
  { to: SUPER_ADMIN.supportInbox, key: "navSupportInbox" },
  { to: SUPER_ADMIN.askSageLog, key: "navAskSageLogPlatform" },
  { to: SUPER_ADMIN.engineQuality, key: "navEngineQuality" },
  { to: SUPER_ADMIN.llmSettings, key: "navLlmSettings" },
];

const ALL_NAV = [
  ...STANDARDS_NAV,
  ...REVIEW_NAV,
  ...OPERATIONS_NAV,
  ...SUPER_ADMIN_NAV,
];

export function navItem(to: string): NavItem {
  const found = ALL_NAV.find((item) => item.to === to);
  if (!found) throw new Error(`Unknown navigation target: ${to}`);
  return found;
}

/** Marks a menu link so the system guide can point at it on screen. */
export const NAV_TARGET_ATTRIBUTE = "data-nav-target";
